import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Paper,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Profile() {
  const { currentUser, updateProfile, updatePassword } = useAuth();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };
  
  // Profile form
  const profileFormik = useFormik({
    initialValues: {
      firstName: 'John',
      lastName: 'Doe',
      email: currentUser?.email || '',
      phone: '(555) 123-4567',
      address: '123 Main St, Anytown, USA'
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      phone: Yup.string(),
      address: Yup.string()
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        
        // In a real app, this would be an API call
        console.log('Updating profile:', values);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setSuccess('Profile updated successfully!');
      } catch (err) {
        setError('Failed to update profile: ' + (err.message || 'Please try again'));
      } finally {
        setLoading(false);
      }
    }
  });
  
  // Password form
  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required('Current password is required'),
      newPassword: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('New password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        .required('Confirm password is required')
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        
        // In a real app, this would be an API call
        console.log('Updating password');
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Reset form
        passwordFormik.resetForm();
        
        setSuccess('Password updated successfully!');
      } catch (err) {
        setError('Failed to update password: ' + (err.message || 'Please try again'));
      } finally {
        setLoading(false);
      }
    }
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true
  });
  
  const handleNotificationChange = (event) => {
    setNotifications({
      ...notifications,
      [event.target.name]: event.target.checked
    });
  };
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Profile
      </Typography>
      
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={value}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Personal Information" />
          <Tab label="Security" />
          <Tab label="Preferences" />
        </Tabs>
        
        {success && (
          <Alert severity="success" sx={{ m: 3 }}>
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ m: 3 }}>
            {error}
          </Alert>
        )}
        
        <TabPanel value={value} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                sx={{ width: 120, height: 120, mb: 2 }}
                alt={`${profileFormik.values.firstName} ${profileFormik.values.lastName}`}
                src="/static/images/avatar/1.jpg"
              />
              <Button variant="outlined" sx={{ mb: 2 }}>
                Change Photo
              </Button>
              <Typography variant="body2" color="text.secondary">
                Recommended size: 200x200px
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <form onSubmit={profileFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="firstName"
                      name="firstName"
                      label="First Name"
                      value={profileFormik.values.firstName}
                      onChange={profileFormik.handleChange}
                      error={profileFormik.touched.firstName && Boolean(profileFormik.errors.firstName)}
                      helperText={profileFormik.touched.firstName && profileFormik.errors.firstName}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="lastName"
                      name="lastName"
                      label="Last Name"
                      value={profileFormik.values.lastName}
                      onChange={profileFormik.handleChange}
                      error={profileFormik.touched.lastName && Boolean(profileFormik.errors.lastName)}
                      helperText={profileFormik.touched.lastName && profileFormik.errors.lastName}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Email Address"
                      value={profileFormik.values.email}
                      onChange={profileFormik.handleChange}
                      error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
                      helperText={profileFormik.touched.email && profileFormik.errors.email}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="phone"
                      name="phone"
                      label="Phone Number"
                      value={profileFormik.values.phone}
                      onChange={profileFormik.handleChange}
                      error={profileFormik.touched.phone && Boolean(profileFormik.errors.phone)}
                      helperText={profileFormik.touched.phone && profileFormik.errors.phone}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="address"
                      name="address"
                      label="Address"
                      value={profileFormik.values.address}
                      onChange={profileFormik.handleChange}
                      error={profileFormik.touched.address && Boolean(profileFormik.errors.address)}
                      helperText={profileFormik.touched.address && profileFormik.errors.address}
                      disabled={loading}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <form onSubmit={passwordFormik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="currentPassword"
                  name="currentPassword"
                  label="Current Password"
                  type="password"
                  value={passwordFormik.values.currentPassword}
                  onChange={passwordFormik.handleChange}
                  error={passwordFormik.touched.currentPassword && Boolean(passwordFormik.errors.currentPassword)}
                  helperText={passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="newPassword"
                  name="newPassword"
                  label="New Password"
                  type="password"
                  value={passwordFormik.values.newPassword}
                  onChange={passwordFormik.handleChange}
                  error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                  helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  value={passwordFormik.values.confirmPassword}
                  onChange={passwordFormik.handleChange}
                  error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                  helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h6" gutterBottom>
            Account Security
          </Typography>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Two-Factor Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add an extra layer of security to your account by enabling two-factor authentication.
              </Typography>
              <Button variant="outlined" sx={{ mt: 1 }}>
                Enable 2FA
              </Button>
            </CardContent>
          </Card>
        </TabPanel>
        
        <TabPanel value={value} index={2}>
          <Typography variant="h6" gutterBottom>
            Notification Preferences
          </Typography>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Email Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Receive notifications about quotes, appointments, and service updates via email.
                  </Typography>
                  <Button 
                    variant={notifications.email ? "contained" : "outlined"} 
                    color={notifications.email ? "primary" : "inherit"}
                    onClick={() => setNotifications({...notifications, email: !notifications.email})}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {notifications.email ? "Enabled" : "Disabled"}
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    SMS Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Receive text message alerts for appointment reminders and updates.
                  </Typography>
                  <Button 
                    variant={notifications.sms ? "contained" : "outlined"} 
                    color={notifications.sms ? "primary" : "inherit"}
                    onClick={() => setNotifications({...notifications, sms: !notifications.sms})}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {notifications.sms ? "Enabled" : "Disabled"}
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Push Notifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Receive push notifications on your device for important updates.
                  </Typography>
                  <Button 
                    variant={notifications.push ? "contained" : "outlined"} 
                    color={notifications.push ? "primary" : "inherit"}
                    onClick={() => setNotifications({...notifications, push: !notifications.push})}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {notifications.push ? "Enabled" : "Disabled"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          <Button variant="contained" onClick={() => setSuccess('Preferences saved successfully!')}>
            Save Preferences
          </Button>
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default Profile;
